import styles from './CropControls.module.css';

/**
 * Get confidence level for styling (high/medium/low)
 */
function getConfidenceLevel(confidence) {
  if (confidence >= 0.75) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

/**
 * Format quality tier for display
 */
function formatQualityTier(tier) {
  if (!tier) return null;
  const tierMap = {
    TIER_1_WIRE_PRINT: 'Wire/Print',
    TIER_2_WEB_EDITORIAL: 'Web/Editorial',
    TIER_3_UNUSABLE: 'Low Quality',
  };
  return tierMap[tier] || tier;
}

/**
 * Crop controls toolbar with tilt slider, aspect ratio, and alternative crops.
 *
 * Single responsibility: Display and control crop parameters.
 * Does not contain business logic - receives all state via props.
 *
 * Props:
 *   cropRotation - Current rotation value in degrees
 *   aspectLabel - Current aspect ratio label (e.g., "3:2")
 *   onCycleAspect - Handler to cycle to next aspect ratio
 *   cropDims - Current crop dimensions {w, h} in source pixels (from getFullFrameCrop)
 *   onResetCrop - Handler to reset crop to saved/detected state
 *   cropAlternatives - List of alternative crop options
 *   rollInfo - Detected roll info from Sentinel {angle_deg, confidence, applied}
 *   onRotationChange - Handler for rotation slider changes
 *   onResetRotation - Handler to reset rotation to 0
 *   onResetToDetected - Handler to reset to detected rotation
 *   onApplyAlternative - Handler to apply an alternative crop
 */
export default function CropControls({
  cropRotation,
  aspectLabel,
  onCycleAspect,
  cropDims,
  onResetCrop,
  cropAlternatives,
  rollInfo,
  onRotationChange,
  onResetRotation,
  onResetToDetected,
  onApplyAlternative,
}) {
  // Detected rotation info
  const detectedAngle = rollInfo?.angle_deg ?? 0;
  const detectedConfidence = rollInfo?.confidence ?? 0;
  const hasDetection = detectedConfidence > 0;
  const confidenceLevel = getConfidenceLevel(detectedConfidence);
  const confidencePercent = Math.round(detectedConfidence * 100);

  // Check if current rotation matches detected
  const isAtDetected = Math.abs(cropRotation - detectedAngle) < 0.1;

  return (
    <div className={styles.controls}>
      {/* Detected rotation indicator */}
      {hasDetection && (
        <>
          <div className={styles.group}>
            <span className={styles.label}>Detected</span>
            <span
              className={`${styles.detectedValue} ${styles[confidenceLevel]}`}
              title={`Auto-detected tilt: ${detectedAngle.toFixed(1)} degrees (${confidencePercent}% confidence)`}
            >
              {detectedAngle >= 0 ? '+' : ''}{detectedAngle.toFixed(1)}deg
            </span>
            <span
              className={`${styles.confidenceBadge} ${styles[confidenceLevel]}`}
              title={`Confidence: ${confidencePercent}%`}
            >
              {confidencePercent}%
            </span>
            {!isAtDetected && (
              <button
                className={styles.detectBtn}
                onClick={onResetToDetected}
                title={`Apply detected rotation (${detectedAngle.toFixed(1)} deg)`}
              >
                Use
              </button>
            )}
          </div>
          <span className={styles.divider}>|</span>
        </>
      )}

      {/* Manual tilt controls */}
      <div className={styles.group}>
        <span className={styles.label}>Tilt</span>
        <input
          type="range"
          className={styles.slider}
          min={-90}
          max={90}
          step={0.5}
          value={cropRotation}
          onChange={(e) => onRotationChange(parseFloat(e.target.value))}
        />
        <span className={styles.value}>{cropRotation.toFixed(1)}deg</span>
        <button
          className={styles.resetBtn}
          onClick={onResetRotation}
          title="Reset tilt to 0 degrees"
        >
          0
        </button>
      </div>

      <span className={styles.divider}>|</span>

      <div className={styles.group}>
        <span className={styles.label}>Aspect</span>
        <button
          className={styles.aspectBtn}
          onClick={onCycleAspect}
          title="Cycle aspect ratio (X)"
        >
          {aspectLabel || '--'}
        </button>
      </div>

      {cropDims && (
        <>
          <span className={styles.divider}>|</span>
          <div className={styles.group}>
            <span className={styles.label}>Crop:</span>
            <span className={styles.value}>
              {cropDims.w} x {cropDims.h}
            </span>
            {onResetCrop && (
              <button
                className={styles.resetBtn}
                onClick={onResetCrop}
                title="Reset crop to detected position"
              >
                Reset
              </button>
            )}
          </div>
        </>
      )}

      {cropAlternatives?.length > 0 && (
        <>
          <span className={styles.divider}>|</span>
          <div className={styles.group}>
            <span className={styles.label}>Alt crops</span>
            {cropAlternatives.map((alt, i) => {
              const qualityLabel = formatQualityTier(alt.quality_tier);
              const tooltipParts = [
                alt.reason || `Option ${i + 1}`,
                `Score: ${((alt.score || 0) * 100).toFixed(0)}%`,
              ];
              if (alt.gaic_score != null) {
                tooltipParts.push(`GAIC: ${alt.gaic_score.toFixed(2)}`);
              }
              if (qualityLabel) {
                tooltipParts.push(`Quality: ${qualityLabel}`);
              }
              return (
                <button
                  key={alt.reason || i}
                  className={styles.altBtn}
                  onClick={() => onApplyAlternative(alt)}
                  title={tooltipParts.join(' | ')}
                >
                  {alt.reason ? alt.reason.replace('_', ' ') : i + 1}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
